import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { Transaction, TransactionCreateInput } from '../../models/transaction';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';

// List transactions
const fetchTransactions = async (page: number = 1) => {
    const { data } = await httpClient.get<PaginatedResponse<Transaction>>(
        `${API_ENDPOINTS.TRADER.TRANSACTIONS.LIST}?page=${page}`
    );
    return data;
};

export const useTransactions = (page: number = 1) => {
    return useQuery<PaginatedResponse<Transaction>, Error>({
        queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST, page],
        queryFn: () => fetchTransactions(page),
    });
};

// Get transaction by ID
const fetchTransaction = async (id: number) => {
    const { data } = await httpClient.get<ApiResponse<Transaction>>(
        API_ENDPOINTS.TRADER.TRANSACTIONS.DETAILS(id.toString())
    );
    return data.data;
};

export const useTransaction = (id: number) => {
    return useQuery<Transaction, Error>({
        queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST, id],
        queryFn: () => fetchTransaction(id),
        enabled: !!id,
    });
};

// Create transaction
const createTransaction = async (input: TransactionCreateInput) => {
    const { data } = await httpClient.post<ApiResponse<Transaction>>(
        API_ENDPOINTS.TRADER.TRANSACTIONS.CREATE,
        input
    );
    return data.data;
};

export const useCreateTransaction = () => {
    const queryClient = useQueryClient();

    return useMutation<Transaction, Error, TransactionCreateInput>({
        mutationFn: createTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST] });
        },
    });
};
